import React, { useMemo, useState, useCallback } from 'react';
import { Eye, CheckCircle, XCircle, AlertTriangle, Calendar, Clock, FileText, Paperclip, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react';
import { formatarMoeda } from '../utils';

const TabelaContasReceber = ({ 
  contasFiltradas, 
  abrirModal, 
  contarArquivos,
  filtroAtivo,
  onClearFilters
}) => {
  const [desktopSort, setDesktopSort] = useState({ field: null, direction: 'asc' });

  const handleDesktopSort = (field) => {
    setDesktopSort((prev) => {
      if (prev.field !== field) return { field, direction: 'asc' };
      if (prev.direction === 'asc') return { field, direction: 'desc' };
      return { field: null, direction: 'asc' };
    });
  };

  const getHeaderSortIcon = (field) => {
    if (desktopSort.field !== field) return <ArrowUpDown className="h-3 w-3 opacity-70" />;
    if (desktopSort.direction === 'asc') return <ChevronUp className="h-3 w-3" />;
    return <ChevronDown className="h-3 w-3" />;
  };

  const getAriaSort = (field) => {
    if (desktopSort.field !== field) return 'none';
    return desktopSort.direction === 'asc' ? 'ascending' : 'descending';
  };

  const parseDate = useCallback((value) => {
    if (!value) return null;
    const parsed = value instanceof Date ? new Date(value) : new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, []);

  const getVencimentoInfo = useCallback((conta) => {
    const vencimento = parseDate(conta.data_vencimento_convertida || conta.dataVencimento);
    if (!vencimento) {
      return {
        date: null,
        diasVencimento: null,
        dataFormatada: 'Sem data',
        textoAuxiliar: 'Data inválida'
      };
    }

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    vencimento.setHours(0, 0, 0, 0);
    const diasVencimento = Math.floor((vencimento - hoje) / (1000 * 60 * 60 * 24));

    let textoAuxiliar = '';
    if (conta.status === 'recebida' || conta.status === 'cancelada' || conta.status === 'recebida_parcialmente') {
      textoAuxiliar = 'Conta processada';
    } else if (diasVencimento < 0) {
      textoAuxiliar = `${Math.abs(diasVencimento)} dias atrasados`;
    } else if (diasVencimento === 0) {
      textoAuxiliar = 'Vence hoje';
    } else {
      textoAuxiliar = `Vence em ${diasVencimento} dias`;
    }

    return {
      date: vencimento,
      diasVencimento,
      dataFormatada: vencimento.toLocaleDateString('pt-BR'),
      textoAuxiliar
    };
  }, [parseDate]);

  const getDataRecebimento = useCallback((conta) => {
    if (!conta.data_recebimento) return null;

    try {
      const dataStr = String(conta.data_recebimento).trim();
      const matchBrasileiro = dataStr.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}):(\d{2}))?/);
      if (matchBrasileiro) {
        const [, dia, mes, ano, hora = '00', minuto = '00'] = matchBrasileiro;
        const dateObj = new Date(`${ano}-${mes}-${dia}T${hora}:${minuto}`);
        if (!isNaN(dateObj.getTime())) {
          return dateObj;
        }
      }
      return conta.data_recebimento;
    } catch {
      return conta.data_recebimento;
    }
  }, []);

  const getSortValue = useCallback((conta, field) => {
    switch (field) {
      case 'cliente':
        return (conta.cliente || conta.fornecedor || '').toLowerCase();
      case 'descricao':
        return (conta.observacao || conta.descricao || '').toLowerCase();
      case 'valor':
        return parseFloat(conta.valor) || 0;
      case 'vencimento':
        if (filtroAtivo === 'recebidas') {
          const dataRec = getDataRecebimento(conta);
          return dataRec instanceof Date ? dataRec.getTime() : 0;
        }
        return getVencimentoInfo(conta).date?.getTime() || 0;
      case 'status':
        return (conta.status || '').toLowerCase();
      default:
        return '';
    }
  }, [filtroAtivo, getVencimentoInfo, getDataRecebimento]);

  const contasExibicao = useMemo(() => {
    if (!desktopSort.field) return contasFiltradas;
    const contasOrdenadas = [...contasFiltradas];
    contasOrdenadas.sort((a, b) => {
      const valueA = getSortValue(a, desktopSort.field);
      const valueB = getSortValue(b, desktopSort.field);
      if (valueA === valueB) return 0;
      const direction = desktopSort.direction === 'asc' ? 1 : -1;
      return valueA > valueB ? direction : -direction;
    });
    return contasOrdenadas;
  }, [contasFiltradas, desktopSort, getSortValue]);

  // Funções de estilo para status e prioridade
  const getStatusInfo = (status) => {
    switch (status) {
      case 'vencida':
        return {
          icon: <XCircle className="h-4 w-4 text-red-500" />,
          label: 'Vencida',
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200'
        };
      case 'proxima_vencimento':
        return {
          icon: <AlertTriangle className="h-4 w-4 text-yellow-500" />,
          label: 'Próxima a vencer',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200'
        };
      case 'hoje':
        return {
          icon: <Calendar className="h-4 w-4 text-orange-500" />,
          label: 'Vence hoje',
          color: 'text-orange-600',
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200'
        };
      case 'proximos_meses':
        return {
          icon: <Clock className="h-4 w-4 text-blue-500" />,
          label: 'Próximos meses',
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200'
        };
      case 'recebida':
        return {
          icon: <CheckCircle className="h-4 w-4 text-green-500" />,
          label: 'Recebida',
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200'
        };
      case 'cancelada':
        return {
          icon: <XCircle className="h-4 w-4 text-[color:var(--text-muted)]" />,
          label: 'Cancelada',
          color: 'text-[color:var(--text-muted)]',
          bgColor: 'bg-[color:var(--surface-muted)]',
          borderColor: 'border-[color:var(--border)]'
        };
      case 'recebida_parcialmente':
        return {
          icon: <Clock className="h-4 w-4 text-blue-500" />,
          label: 'Recebida Parcial',
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200'
        };
      default:
        return {
          icon: <CheckCircle className="h-4 w-4 text-green-500" />,
          label: 'Em dia',
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200'
        };
    }
  };

  const getPrioridadeInfo = (prioridade) => {
    switch (prioridade) {
      case 'alta':
        return {
          label: 'Alta',
          color: 'text-red-600',
          bgColor: 'bg-red-100'
        };
      case 'media':
        return {
          label: 'Média',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-100'
        };
      case 'baixa':
        return {
          label: 'Baixa',
          color: 'text-green-600',
          bgColor: 'bg-green-100'
        };
      default:
        return {
          label: 'Baixa',
          color: 'text-[color:var(--text-muted)]',
          bgColor: 'bg-gray-100'
        };
    }
  };

  const renderArquivos = (conta) => {
    const quantidadeArquivos = contarArquivos(conta);
    
    if (quantidadeArquivos === 0) {
      return (
        <div className="flex items-center justify-center">
          <div className="text-[color:var(--text-muted)]">
            <FileText className="h-4 w-4" />
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center">
        <div className="relative">
          <Paperclip className="h-4 w-4 text-blue-600" />
          {quantidadeArquivos > 1 && (
            <div className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full h-3 w-3 flex items-center justify-center font-medium text-[10px]">
              {quantidadeArquivos}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Função para renderizar card mobile
  const renderCardMobile = (conta) => {
    const vencimentoInfo = getVencimentoInfo(conta);
    const diasVencimento = vencimentoInfo.diasVencimento;
    const status = conta.status;
    let prioridade = 'baixa';
    if (status !== 'recebida' && status !== 'recebida_parcialmente' && status !== 'cancelada' && typeof diasVencimento === 'number') {
      if (diasVencimento < 0) {
        prioridade = 'alta';
      } else if (diasVencimento <= 3) {
        prioridade = 'media';
      }
    }
    
    const statusInfo = getStatusInfo(status, diasVencimento);
    const prioridadeInfo = getPrioridadeInfo(prioridade);

    const abrirDetalhesConta = () => abrirModal(conta);
    const handleCardKeyDown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        abrirDetalhesConta();
      }
    };

    return (
      <div
        key={conta.id}
        role="button"
        tabIndex={0}
        onClick={abrirDetalhesConta}
        onKeyDown={handleCardKeyDown}
        className="mb-3 cursor-pointer rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] p-4 shadow-sm transition-colors hover:bg-[color:var(--surface-muted)]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
        aria-label={`Abrir detalhes da conta ${conta.id}`}
      >
        <div className="mb-3 flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-[color:var(--text-muted)]">#{conta.id}</span>
              <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${statusInfo.bgColor} ${statusInfo.color} ${statusInfo.borderColor}`}>
                {statusInfo.icon}
                <span className="ml-1">{statusInfo.label}</span>
              </span>
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${prioridadeInfo.bgColor} ${prioridadeInfo.color}`}>
                {prioridadeInfo.label}
              </span>
            </div>
            <h3 className="mb-1 truncate text-sm font-semibold text-[color:var(--text-primary)]">{conta.cliente || conta.fornecedor || 'Cliente não informado'}</h3>
            <p className="text-xs text-[color:var(--text-muted)]">
              {conta.planejamento || 'Sem planejamento'} › {conta.conta_analitica || 'Sem subcategoria'}
            </p>
          </div>
          <div className="ml-2 flex items-center gap-2">
            {renderArquivos(conta)}
            <button 
              onClick={(event) => {
                event.stopPropagation();
                abrirDetalhesConta();
              }}
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] text-[color:var(--accent)] transition-colors hover:bg-[color:var(--surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35" 
              title="Visualizar"
              aria-label={`Visualizar detalhes da conta ${conta.id}`}
            >
              <Eye className="h-4 w-4" />
            </button>
          </div>
        </div>

        <p className="mb-3 text-sm text-[color:var(--text-primary)] line-clamp-2">
          {conta.observacao || conta.descricao}
        </p>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-[color:var(--text-muted)] mb-1">Valor</p>
            <p className="text-sm font-semibold text-[color:var(--text-primary)]">
              {formatarMoeda(parseFloat(conta.valor) || 0)}
            </p>
          </div>
          <div>
            <p className="text-xs text-[color:var(--text-muted)] mb-1">Vencimento</p>
            <p className="text-sm font-medium text-[color:var(--text-primary)]">
              {vencimentoInfo.dataFormatada}
            </p>
            {vencimentoInfo.textoAuxiliar && (
              <p className={`text-xs ${
                typeof diasVencimento === 'number' && diasVencimento < 0 ? 'text-red-600' :
                typeof diasVencimento === 'number' && diasVencimento <= 7 ? 'text-yellow-600' :
                'text-[color:var(--text-muted)]'
              }`}>
                {vencimentoInfo.textoAuxiliar}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-lg overflow-hidden relative">
      {/* Versão Desktop - Tabela */}
      <div className="hidden md:block overflow-y-auto max-h-96">
        <table className="w-full table-fixed divide-y divide-[color:var(--border)]" style={{ tableLayout: 'fixed', width: '100%' }}>
          <thead className="bg-[color:var(--surface-muted)] sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-[color:var(--text-muted)] tracking-wide w-[5%]">
                ID
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-[color:var(--text-muted)] tracking-wide w-[15%]" aria-sort={getAriaSort('cliente')}>
                <button type="button" className="inline-flex items-center gap-1 hover:text-[color:var(--text-primary)] transition-colors" onClick={() => handleDesktopSort('cliente')}>
                  Cliente
                  {getHeaderSortIcon('cliente')}
                </button>
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-[color:var(--text-muted)] tracking-wide w-[18%]" aria-sort={getAriaSort('descricao')}>
                <button type="button" className="inline-flex items-center gap-1 hover:text-[color:var(--text-primary)] transition-colors" onClick={() => handleDesktopSort('descricao')}>
                  Descrição
                  {getHeaderSortIcon('descricao')}
                </button>
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-[color:var(--text-muted)] tracking-wide w-[8%]" aria-sort={getAriaSort('valor')}>
                <button type="button" className="inline-flex items-center gap-1 hover:text-[color:var(--text-primary)] transition-colors" onClick={() => handleDesktopSort('valor')}>
                  Valor
                  {getHeaderSortIcon('valor')}
                </button>
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-[color:var(--text-muted)] tracking-wide w-[10%]" aria-sort={getAriaSort('vencimento')}>
                <button type="button" className="inline-flex items-center gap-1 hover:text-[color:var(--text-primary)] transition-colors" onClick={() => handleDesktopSort('vencimento')}>
                  {filtroAtivo === 'recebidas' ? 'Data de Recebimento' : 'Vencimento'}
                  {getHeaderSortIcon('vencimento')}
                </button>
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-[color:var(--text-muted)] tracking-wide w-[10%]" aria-sort={getAriaSort('status')}>
                <button type="button" className="inline-flex items-center gap-1 hover:text-[color:var(--text-primary)] transition-colors" onClick={() => handleDesktopSort('status')}>
                  Status
                  {getHeaderSortIcon('status')}
                </button>
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-[color:var(--text-muted)] tracking-wide w-[8%]">
                Prioridade
              </th>
              <th className="px-3 py-2 pr-1 text-left text-xs font-medium text-[color:var(--text-muted)] tracking-wide w-[5%]">
                Arquivos
              </th>
              <th className="px-3 py-2 pl-4 text-left text-xs font-medium text-[color:var(--text-muted)] tracking-wide w-[5%]">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-[color:var(--surface)] divide-y divide-[color:var(--border)]">
            {contasExibicao.map((conta) => {
              const vencimentoInfo = getVencimentoInfo(conta);
              const diasVencimento = vencimentoInfo.diasVencimento;
              
              const status = conta.status;
              
              let prioridade = 'baixa';
              if (status !== 'recebida' && status !== 'recebida_parcialmente' && status !== 'cancelada' && typeof diasVencimento === 'number') {
                if (diasVencimento < 0) {
                  prioridade = 'alta';
                } else if (diasVencimento <= 3) {
                  prioridade = 'media';
                }
              }
              
              const statusInfo = getStatusInfo(status, diasVencimento);
              const prioridadeInfo = getPrioridadeInfo(prioridade);
              
              return (
                <tr key={conta.id} className="hover:bg-[color:var(--surface-muted)]">
                  <td className="px-3 py-2">
                    <span className="font-mono text-xs text-[color:var(--text-muted)]">#{conta.id}</span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-xs font-medium text-[color:var(--text-primary)] truncate" title={conta.cliente || conta.fornecedor}>
                      {conta.cliente || conta.fornecedor}
                    </div>
                    <div className="text-xs text-[color:var(--text-muted)] truncate" title={`${conta.planejamento || 'Sem planejamento'} › ${conta.conta_analitica || 'Sem subcategoria'}`}>
                      {(conta.planejamento || 'Sem planejamento')} <span className="text-[color:var(--text-muted)]">›</span> {(conta.conta_analitica || 'Sem subcategoria')}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-xs text-[color:var(--text-primary)] break-words" title={conta.observacao || conta.descricao}>
                      {conta.observacao || conta.descricao}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-xs font-medium text-[color:var(--text-primary)] text-right">
                      {formatarMoeda(parseFloat(conta.valor) || 0)}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {filtroAtivo === 'recebidas' ? (
                      <div className="text-xs text-[color:var(--text-primary)]">
                        {(() => {
                          const dataRec = getDataRecebimento(conta);
                          if (!dataRec) return 'N/A';
                          if (dataRec instanceof Date) {
                            const dataFormatada = dataRec.toLocaleDateString('pt-BR');
                            const horaFormatada = dataRec.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                            return horaFormatada !== '00:00' ? `${dataFormatada} ${horaFormatada}` : dataFormatada;
                          }
                          return dataRec;
                        })()}
                      </div>
                    ) : (
                      <>
                        <div className="text-xs text-[color:var(--text-primary)]">
                          {vencimentoInfo.dataFormatada}
                        </div>
                        {vencimentoInfo.textoAuxiliar && (
                          <div className={`text-xs ${
                            typeof diasVencimento === 'number' && diasVencimento < 0 ? 'text-red-600' :
                            typeof diasVencimento === 'number' && diasVencimento <= 7 ? 'text-yellow-600' :
                            'text-[color:var(--text-muted)]'
                          }`}>
                            {vencimentoInfo.textoAuxiliar}
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.bgColor} ${statusInfo.color} ${statusInfo.borderColor} border`}>
                      {statusInfo.icon}
                      <span className="ml-1 hidden sm:inline">{statusInfo.label}</span>
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${prioridadeInfo.bgColor} ${prioridadeInfo.color}`}>
                      {prioridadeInfo.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 pr-1 text-sm text-[color:var(--text-primary)] text-center">
                    {renderArquivos(conta)}
                  </td>
                  <td className="px-3 py-2 pl-4">
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={() => abrirModal(conta)}
                        className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] text-[color:var(--accent)] transition-colors hover:bg-[color:var(--surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
                        title="Visualizar"
                        aria-label={`Visualizar detalhes da conta ${conta.id}`}
                      >
                        <Eye className="h-3 w-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Versão Mobile - Cards */}
      <div className="md:hidden p-3">
        {contasFiltradas.length === 0 ? (
          <div className="rounded-lg border border-dashed border-[color:var(--border)] bg-[color:var(--surface-muted)]/45 px-4 py-8 text-center text-[color:var(--text-muted)]">
            <p className="text-sm font-medium">Nenhuma conta encontrada para os filtros atuais.</p>
            {onClearFilters && (
              <button
                type="button"
                onClick={onClearFilters}
                className="mt-3 inline-flex min-h-[44px] items-center rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-2 text-sm font-medium text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
              >
                Limpar filtros
              </button>
            )}
          </div>
        ) : (
          contasFiltradas.map(conta => renderCardMobile(conta))
        )}
      </div>
    </div>
  );
};

export default TabelaContasReceber;
